import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

import { alertsApi } from '../../services/api';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';

interface Alert {
  id: string;
  type: string;
  severity: string;
  title: string;
  description: string | null;
  status: string;
  latitude: number | null;
  longitude: number | null;
  createdAt: string;
  readAt: string | null;
  device: {
    id: string;
    imei: string;
    name: string | null;
  };
  readBy?: {
    id: string;
    name: string;
  };
}

const severityColors: Record<string, string> = {
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  HIGH: 'bg-orange-100 text-orange-800 border-orange-200',
  CRITICAL: 'bg-red-100 text-red-800 border-red-200',
};

const statusColors: Record<string, string> = {
  NEW: 'bg-red-100 text-red-800',
  READ: 'bg-gray-100 text-gray-800',
  ACKNOWLEDGED: 'bg-blue-100 text-blue-800',
  RESOLVED: 'bg-green-100 text-green-800',
};

const statusLabels: Record<string, string> = {
  NEW: 'Nuevo',
  READ: 'Leido',
  ACKNOWLEDGED: 'Reconocido',
  RESOLVED: 'Resuelto',
};

const typeLabels: Record<string, string> = {
  CONNECTION_LOST: 'Conexión Perdida',
  SPEED_EXCEEDED: 'Exceso de Velocidad',
  GEOFENCE_EXIT: 'Salida de Geocerca',
  GEOFENCE_ENTER: 'Entrada a Geocerca',
  DEVICE_OFFLINE: 'Dispositivo Offline',
  LOW_BATTERY: 'Bateria Baja',
};

const severityLabels: Record<string, string> = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
  CRITICAL: 'Critica',
};

export function AlertsPage() {
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['alerts', selectedType, selectedStatus, selectedSeverity],
    queryFn: async () => {
      const params: Record<string, unknown> = { limit: 100 };
      if (selectedType) params.type = selectedType;
      if (selectedStatus) params.status = selectedStatus;
      if (selectedSeverity) params.severity = selectedSeverity;
      const response = await alertsApi.list(params);
      return response.data;
    },
  });

  // Unread count query
  const { data: unreadData } = useQuery({
    queryKey: ['alertsUnreadCount'],
    queryFn: async () => {
      const response = await alertsApi.getUnreadCount();
      return response.data.data;
    },
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => alertsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertsUnreadCount'] });
    },
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: string) => alertsApi.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertsUnreadCount'] });
      setShowDetailModal(false);
    },
  });

  const resolveMutation = useMutation({
    mutationFn: (id: string) => alertsApi.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertsUnreadCount'] });
      setShowDetailModal(false);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => alertsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alertsUnreadCount'] });
    },
  });

  const alerts = (data?.data || []) as Alert[];
  const pagination = data?.pagination;
  const unreadCount = unreadData?.count || 0;

  const handleViewDetail = (alert: Alert) => {
    setSelectedAlert(alert);
    setShowDetailModal(true);
    // Mark as read when opening detail
    if (alert.status === 'NEW') {
      markReadMutation.mutate(alert.id);
    }
  };

  const clearFilters = () => {
    setSelectedType('');
    setSelectedStatus('');
    setSelectedSeverity('');
  };

  const hasFilters = selectedType || selectedStatus || selectedSeverity;

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas</h1>
          <p className="text-gray-600">
            Historial de alertas del sistema
            {unreadCount > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">
                {unreadCount} sin leer
              </span>
            )}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="secondary"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            {markAllReadMutation.isPending ? 'Marcando...' : 'Marcar todas como leidas'}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label htmlFor="alerts-type" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              id="alerts-type"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {Object.entries(typeLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="alerts-status" className="block text-sm font-medium text-gray-700 mb-1">
              Estado
            </label>
            <select
              id="alerts-status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todos</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="alerts-severity" className="block text-sm font-medium text-gray-700 mb-1">
              Severidad
            </label>
            <select
              id="alerts-severity"
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="w-48 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Todas</option>
              {Object.entries(severityLabels).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {hasFilters && (
            <Button variant="ghost" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          )}
        </div>
        {pagination && (
          <div className="mt-2 text-sm text-gray-500">
            Mostrando {alerts.length} de {pagination.total} alertas
          </div>
        )}
      </div>

      {/* Alerts Table */}
      {isLoading ? (
        <div className="flex justify-center py-10">
          <LoadingSpinner size="lg" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500 text-center py-10">
            No hay alertas para mostrar
          </p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dispositivo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titulo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severidad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alerts.map((alert) => (
                <tr
                  key={alert.id}
                  className={`hover:bg-gray-50 ${alert.status === 'NEW' ? 'bg-red-50' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {typeLabels[alert.type] || alert.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/devices/${alert.device.id}`}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800"
                    >
                      {alert.device.name || alert.device.imei}
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{alert.title}</div>
                    {alert.description && (
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {alert.description}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        severityColors[alert.severity] || 'bg-gray-100'
                      }`}
                    >
                      {severityLabels[alert.severity] || alert.severity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        statusColors[alert.status] || 'bg-gray-100'
                      }`}
                    >
                      {statusLabels[alert.status] || alert.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div>{format(new Date(alert.createdAt), 'dd/MM/yyyy HH:mm')}</div>
                    <div className="text-xs text-gray-400">
                      {formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true, locale: es })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewDetail(alert)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Ver
                      </button>
                      {alert.status === 'NEW' && (
                        <button
                          onClick={() => markReadMutation.mutate(alert.id)}
                          disabled={markReadMutation.isPending}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Marcar leido
                        </button>
                      )}
                      {(alert.status === 'NEW' || alert.status === 'READ') && (
                        <button
                          onClick={() => acknowledgeMutation.mutate(alert.id)}
                          disabled={acknowledgeMutation.isPending}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Reconocer
                        </button>
                      )}
                      {alert.status !== 'RESOLVED' && (
                        <button
                          onClick={() => resolveMutation.mutate(alert.id)}
                          disabled={resolveMutation.isPending}
                          className="text-green-600 hover:text-green-900"
                        >
                          Resolver
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Alert Detail Modal */}
      <Modal
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        title="Detalle de Alerta"
      >
        {selectedAlert && (
          <div className="space-y-4">
            {/* Severity badge */}
            <div className={`p-3 rounded-lg border ${severityColors[selectedAlert.severity]}`}>
              <div className="flex justify-between items-start">
                <div>
                  <span className="font-medium">{severityLabels[selectedAlert.severity]}</span>
                  <span className="mx-2">-</span>
                  <span>{typeLabels[selectedAlert.type] || selectedAlert.type}</span>
                </div>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    statusColors[selectedAlert.status]
                  }`}
                >
                  {statusLabels[selectedAlert.status]}
                </span>
              </div>
            </div>

            {/* Title and description */}
            <div>
              <h3 className="font-medium text-gray-900">{selectedAlert.title}</h3>
              {selectedAlert.description && (
                <p className="mt-1 text-gray-600">{selectedAlert.description}</p>
              )}
            </div>

            {/* Device info */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Dispositivo</h4>
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium">{selectedAlert.device.name || 'Sin nombre'}</p>
                  <p className="text-sm text-gray-500">{selectedAlert.device.imei}</p>
                </div>
                <Link
                  to={`/devices/${selectedAlert.device.id}`}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Ver dispositivo
                </Link>
              </div>
            </div>

            {/* Location */}
            {selectedAlert.latitude && selectedAlert.longitude && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Ubicacion</h4>
                <p className="text-sm text-gray-600">
                  {selectedAlert.latitude.toFixed(6)}, {selectedAlert.longitude.toFixed(6)}
                </p>
                <a
                  href={`https://www.google.com/maps?q=${selectedAlert.latitude},${selectedAlert.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  Ver en Google Maps
                </a>
              </div>
            )}

            {/* Timestamps */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Fechas</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Creado:</span>
                  <span>{format(new Date(selectedAlert.createdAt), 'dd/MM/yyyy HH:mm:ss')}</span>
                </div>
                {selectedAlert.readAt && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Leido:</span>
                    <span>{format(new Date(selectedAlert.readAt), 'dd/MM/yyyy HH:mm:ss')}</span>
                  </div>
                )}
                {selectedAlert.readBy && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Leido por:</span>
                    <span>{selectedAlert.readBy.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="border-t pt-4 flex justify-end gap-3">
              {(selectedAlert.status === 'NEW' || selectedAlert.status === 'READ') && (
                <Button
                  variant="secondary"
                  onClick={() => acknowledgeMutation.mutate(selectedAlert.id)}
                  disabled={acknowledgeMutation.isPending}
                >
                  {acknowledgeMutation.isPending ? 'Reconociendo...' : 'Reconocer'}
                </Button>
              )}
              {selectedAlert.status !== 'RESOLVED' && (
                <Button
                  onClick={() => resolveMutation.mutate(selectedAlert.id)}
                  disabled={resolveMutation.isPending}
                >
                  {resolveMutation.isPending ? 'Resolviendo...' : 'Marcar como Resuelto'}
                </Button>
              )}
              {selectedAlert.status === 'RESOLVED' && (
                <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                  Cerrar
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
